import React, { useRef, useEffect, useState } from 'react';
import { TransformedLayer } from '../utils/resourceUtils';
import { renderLayerOnFabricCanvas, applyPatternFill } from '../utils/renderLayer';
import { moveToIndex } from '../utils/imageUtils';
import { enableTextWarping, disableTextWarping } from './textWarp_copy';
import { enable3dEffect, disable3dEffect } from './text3d';
import { enableSplineWarp, disableSplineWarp } from './textSpline';
import { enableDotsEffect, disableDotsEffect } from './textDots';
import { enableBezierWarp, disableBezierWarp } from './textBezier';
import { enableMultiColor, disableMultiColor } from './textMultiColor';
import { enableCircularWarp, disableCircularWarp } from './textCircular';
import { enableLiquidWarp, disableLiquidWarp } from './textLiquid';
import { getEffectInfo } from '../utils/renderLayer';
import {
  Canvas as FabricCanvas,
  Image as FabricImage,
  Rect as FabricRect,
  Circle as FabricCircle,
  Ellipse as FabricEllipse,
  Textbox as FabricTextbox,
  IText as FabricIText,
  Polygon as FabricPolygon,
  Gradient as FabricGradient,
  Pattern as FabricPattern
} from 'fabric';
import { requestAIWithCache } from '../utils/requestAIWithCache';

interface CanvasProps {
  canvasSize: { width: number; height: number };
  selectedColorObj?: { hex: string };
  transformedLayers: TransformedLayer[];
  selectedLayerId: string | null;
  printArea: any;
  onCanvasClick: (e: React.MouseEvent) => void;
  onLayerClick: (layerId: string, e: React.MouseEvent) => void;
  onDragStart: (e: React.MouseEvent, layerId: string) => void;
  onTransformStart: (e: React.MouseEvent, controlPoint: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right') => void;
  elements: any[];
  selectedElement: string | null;
  onElementClick: (elementId: string) => void;
  // canvasRef: React.RefObject<HTMLDivElement | null>;
  onTouchStart?: (e: React.TouchEvent) => void;
  onTouchMove?: (e: React.TouchEvent) => void;
  onTouchEnd?: () => void;
  isMobile?: boolean;
  onLayerTouchStart?: (e: React.TouchEvent, layerId: string) => void;
  onLayerTouchMove?: (e: React.TouchEvent, layerId: string) => void;
  onLayerTouchEnd?: (e: React.TouchEvent, layerId: string) => void;
  onLayerTransformStart?: (e: React.TouchEvent, layerId: string, controlPoint: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'top' | 'bottom' | 'left' | 'right') => void;
  onUpdateLayerPosition?: (layerId: string, left: number, top: number) => void;
  onUpdateLayerSize?: (layerId: string, width: number, height: number) => void;
  onUpdateLayerRotation?: (layerId: string, rotationDeg: number) => void;
}

const Canvas: React.FC<CanvasProps> = ({
  canvasSize,
  selectedColorObj,
  transformedLayers,
  selectedLayerId,
  printArea,
  onCanvasClick,
  onLayerClick,
  onDragStart,
  onTransformStart,
  elements,
  selectedElement,
  onElementClick,
  // canvasRef,
  onTouchStart,
  onTouchMove,
  onTouchEnd,
  isMobile = false,
  onLayerTouchStart,
  onLayerTouchMove,
  onLayerTouchEnd,
  onLayerTransformStart,
  onUpdateLayerPosition,
  onUpdateLayerSize,
  onUpdateLayerRotation
}) => {
  const layersCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fabricCanvasRef = useRef<FabricCanvas | null>(null);

  useEffect(() => {
    if (!layersCanvasRef.current) return;
    const canvas = new FabricCanvas(layersCanvasRef.current, { 
      preserveObjectStacking: true,
      backgroundColor: 'rgba(0,0,0,0)' // 设置画布背景透明
    });
    fabricCanvasRef.current = canvas;
    canvas.setWidth(canvasSize.width);
    canvas.setHeight(canvasSize.height);
    const hideLinearScaleControls = (o: any) => {
      try { o?.setControlsVisibility?.({ ml: false, mr: false, mt: false, mb: false }); } catch { }
    };
    const initFromTransformed = async () => {
      try {
        if (!Array.isArray(transformedLayers) || transformedLayers.length === 0) return;
        console.log("initFromTransformed", transformedLayers);
        for (let idx = 0; idx < transformedLayers.length; idx++) {
          await renderLayerOnFabricCanvas(fabricCanvasRef.current, transformedLayers[idx], idx);
        }
        await new Promise((r) => setTimeout(r, 1000));
        pushHistory();
      } catch { }
    };
    initFromTransformed();
    const handler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const t = d.shape as string;
      if (!fabricCanvasRef.current) return;
      let obj: any = null;
      if (t === 'rectangle' || t === 'square') {
        obj = new FabricRect({ left: 120, top: 120, width: 160, height: t === 'square' ? 160 : 120, fill: '#68aeb0', stroke: '#000000', strokeWidth: 0, originX: 'center', originY: 'center' });
      } else if (t === 'circle') {
        obj = new FabricCircle({ left: 140, top: 140, radius: 80, fill: '#68aeb0', stroke: '#000000', strokeWidth: 0, originX: 'center', originY: 'center' });
      } else if (t === 'oval') {
        obj = new FabricEllipse({ left: 140, top: 140, rx: 100, ry: 60, fill: '#68aeb0', stroke: '#000000', strokeWidth: 0, originX: 'center', originY: 'center' });
      } else if (t === 'triangle') {
        const w = 180, h = 160;
        const points = [{ x: 0, y: -h / 2 }, { x: w / 2, y: h / 2 }, { x: -w / 2, y: h / 2 }];
        obj = new FabricPolygon(points as any, { left: 140, top: 140, fill: '#68aeb0', stroke: '#000000', strokeWidth: 0, originX: 'center', originY: 'center' } as any);
      } else {
        obj = new FabricRect({ left: 120, top: 120, width: 160, height: 120, fill: '#68aeb0', stroke: '#000000', strokeWidth: 0, originX: 'center', originY: 'center' });
      }
      hideLinearScaleControls(obj);
      fabricCanvasRef.current.add(obj);
      pushHistory();
    };
    window.addEventListener('giftmkr:addShape', handler as EventListener);
    const history = { stack: [] as any[], index: -1, max: 50 };
    const pushHistory = () => {
      try {
        const snap = canvas.toJSON();
        const head = history.stack.slice(0, history.index + 1);
        head.push(snap);
        while (head.length > history.max) head.shift();
        history.stack = head;
        history.index = head.length - 1;
        const ev = new CustomEvent('giftmkr:historyChanged', { detail: { index: history.index, length: history.stack.length } });
        window.dispatchEvent(ev);
        const objs = transformCanvasObjectsForSnapshot(canvas);
        if (objs.length > 0) {
          const evLayers = new CustomEvent('giftmkr:layersSnapshot', { detail: { objects: objs } });
          window.dispatchEvent(evLayers);
        }
      } catch { }
    };
    const applyHistory = async (idx: number) => {
      const snap = history.stack[idx];
      if (!snap) return;
      // @ts-ignore
      await canvas.loadFromJSON(snap);
      canvas.requestRenderAll();
    };
    pushHistory();
    const undoHandler = async () => {
      if (history.index <= 0) return;
      history.index -= 1;
      await applyHistory(history.index);
      const ev = new CustomEvent('giftmkr:historyChanged', { detail: { index: history.index, length: history.stack.length } });
      window.dispatchEvent(ev);
    };
    const redoHandler = async () => {
      if (history.index >= history.stack.length - 1) return;
      history.index += 1;
      await applyHistory(history.index);
      const ev = new CustomEvent('giftmkr:historyChanged', { detail: { index: history.index, length: history.stack.length } });
      window.dispatchEvent(ev);
    };
    window.addEventListener('giftmkr:undo', undoHandler as EventListener);
    window.addEventListener('giftmkr:redo', redoHandler as EventListener);
    const bringToFrontHandler = () => {
      const obj = canvas.getActiveObject();
      if (!obj) return;
      const objs = canvas.getObjects();
      const currentIndex = objs.indexOf(obj);
      if (currentIndex < objs.length - 1) {
        // canvas.moveObjectTo(obj, currentIndex + 1); // Fabric.js v6
        // 兼容处理：使用自定义 moveToIndex 或 Fabric API
        moveToIndex(canvas, obj, currentIndex + 1);
      }
      obj.setCoords();
      hideLinearScaleControls(obj);
      canvas.requestRenderAll();
      pushHistory();
    };
    const sendToBackHandler = () => {
      const obj = canvas.getActiveObject();
      if (!obj) return;
      const objs = canvas.getObjects();
      const currentIndex = objs.indexOf(obj);
      if (currentIndex > 0) {
        // canvas.moveObjectTo(obj, currentIndex - 1); // Fabric.js v6
        // 兼容处理
        moveToIndex(canvas, obj, currentIndex - 1);
      }
      obj.setCoords();
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:bringToFront', bringToFrontHandler as EventListener);
    window.addEventListener('giftmkr:sendToBack', sendToBackHandler as EventListener);
    const imageHandler = async (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const url = d.url || 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=400&fit=crop&crop=center';
      const borderColor = d.borderColor || '#000000';
      const borderSize = typeof d.borderSize === 'number' ? d.borderSize : 4;
      try {
        const img = await FabricImage.fromURL(url, { crossOrigin: 'anonymous' } as any);
        const targetWidth = 200;
        const targetHeight = 200;
        const iw = (img.width || targetWidth);
        const ih = (img.height || targetHeight);
        const s = Math.min(targetWidth / iw, targetHeight / ih);
        const displayedWidth = iw * s;
        const displayedHeight = ih * s;
        img.set({ left: 200, top: 150, originX: 'center', originY: 'center', scaleX: s, scaleY: s } as any);
        hideLinearScaleControls(img);
        fabricCanvasRef.current?.add(img);
        pushHistory();
      } catch { }
    };
    window.addEventListener('giftmkr:addImage', imageHandler as EventListener);
    const bgImageHandler = async (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const url = d.url || '';
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      try {
        if (!url) {
          canvas.backgroundImage = undefined;
          return canvas.requestRenderAll();
        }
        const img = await FabricImage.fromURL(url + "?x-oss-process=style/large", { crossOrigin: 'anonymous' } as any);
        const iw = img.width || 1;
        const ih = img.height || 1;
        const cw = canvas.getWidth();
        const ch = canvas.getHeight();
        const s = Math.max(cw / iw, ch / ih);
        img.set({ originX: 'center', originY: 'center', left: cw / 2, top: ch / 2, scaleX: s, scaleY: s } as any);
        canvas.backgroundImage = img;
        canvas.requestRenderAll();
        pushHistory();
      } catch { }
    };
    window.addEventListener('giftmkr:setBackgroundImage', bgImageHandler as EventListener);
    const bgTextureHandler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const key = d.texture || '';
      const cw = fabricCanvasRef.current?.getWidth() ?? 0;
      const ch = fabricCanvasRef.current?.getHeight() ?? 0;
      if (!fabricCanvasRef.current) return;
      if (!key) {
        fabricCanvasRef.current.backgroundColor = '';
        fabricCanvasRef.current.requestRenderAll();
        return;
      }
      const factories: Record<string, () => HTMLCanvasElement> = {
        'lines-horizontal': () => {
          const tile = document.createElement('canvas');
          tile.width = 32; tile.height = 32;
          const ctx = tile.getContext('2d');
          if (!ctx) return tile;
          ctx.fillStyle = '#f4f5f7';
          ctx.fillRect(0, 0, 32, 32);
          ctx.strokeStyle = '#e9ecef';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(0, 16);
          ctx.lineTo(32, 16);
          ctx.stroke();
          return tile;
        },
        'lines-vertical': () => {
          const tile = document.createElement('canvas');
          tile.width = 32; tile.height = 32;
          const ctx = tile.getContext('2d');
          if (!ctx) return tile;
          ctx.fillStyle = '#f4f5f7';
          ctx.fillRect(0, 0, 32, 32);
          ctx.strokeStyle = '#e9ecef';
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.moveTo(16, 0);
          ctx.lineTo(16, 32);
          ctx.stroke();
          return tile;
        },
        'dots': () => {
          const tile = document.createElement('canvas');
          tile.width = 24; tile.height = 24;
          const ctx = tile.getContext('2d');
          if (!ctx) return tile;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, 24, 24);
          ctx.fillStyle = '#e0e0e0';
          ctx.beginPath();
          ctx.arc(12, 12, 2, 0, Math.PI * 2);
          ctx.fill();
          return tile;
        },
        'checkerboard': () => {
          const tile = document.createElement('canvas');
          tile.width = 20; tile.height = 20;
          const ctx = tile.getContext('2d');
          if (!ctx) return tile;
          ctx.fillStyle = '#ffffff';
          ctx.fillRect(0, 0, 20, 20);
          ctx.fillStyle = '#eceff1';
          ctx.fillRect(0, 0, 10, 10);
          ctx.fillRect(10, 10, 10, 10);
          return tile;
        },
        'default': () => {
          const tile = document.createElement('canvas');
          tile.width = 32; tile.height = 32;
          const ctx = tile.getContext('2d');
          if (!ctx) return tile;
          const grd = ctx.createLinearGradient(0, 0, 32, 0);
          grd.addColorStop(0, '#f0f2f5');
          grd.addColorStop(1, '#e3e6ea');
          ctx.fillStyle = grd;
          ctx.fillRect(0, 0, 32, 32);
          return tile;
        }
      };
      const factory = factories[key] || factories['default'];
      const tile = factory();
      const pattern = new (FabricPattern as any)({ source: tile, repeat: 'repeat' });
      fabricCanvasRef.current!.backgroundImage = undefined;
      fabricCanvasRef.current.backgroundColor = pattern as any;
      fabricCanvasRef.current.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:setBackgroundTexture', bgTextureHandler as EventListener);

    const bgColorHandler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const color = d.color || '';
      if (!fabricCanvasRef.current) return;
      fabricCanvasRef.current!.backgroundImage = undefined;
      fabricCanvasRef.current.backgroundColor = color;
      fabricCanvasRef.current.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:setBackgroundColor', bgColorHandler as EventListener);

    const textHandler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const t = d.text || 'Text';
      const fs = 50;
      const ff = d.fontFamily || 'Arial';
      const fill = d.fill || '#000000';
      const tb = new FabricIText(t, { stroke: '#000000', strokeWidth: 0, left: 200, top: 150, fontSize: fs, fontFamily: ff, fill, originX: 'center', originY: 'center' } as any);
      hideLinearScaleControls(tb);
      fabricCanvasRef.current?.add(tb);
      pushHistory();
    };
    window.addEventListener('giftmkr:addText', textHandler as EventListener);

    const updateActiveHandler = async (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      console.log("updateActiveHandler..........", d)
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj: any = canvas.getActiveObject();
      const currrentLayerIndex = canvas.getObjects().indexOf(obj);
      if (!obj) return;
      if (typeof d.fill !== 'undefined') obj.set({ fill: d.fill });
      if (typeof d.stroke !== 'undefined') obj.set({ stroke: d.stroke });
      if (typeof d.strokeWidth === 'number') obj.set({ strokeWidth: d.strokeWidth });
      if (typeof d.angle === 'number' && Number.isFinite(d.angle)) {
        const norm = ((d.angle % 360) + 360) % 360;
        obj.set({ angle: norm });
      }
      if (typeof d.fillImageUrl === 'string' && d.fillImageUrl && obj.type !== 'image') {
        try {
          // 调用公共函数处理图案填充
          applyPatternFill(obj, d.fillImageUrl, d.patternRepeat || 'no-repeat').then(() => {
            canvas.requestRenderAll();
            pushHistory();
          });
        } catch { }
      }
      if (typeof d.type === 'string' && d.type && d.type !== obj.type) {
        const canvasWidth = canvas.getWidth();
        const canvasHeight = canvas.getHeight();
        const cx = obj.left;
        const cy = obj.top;
        const w = typeof obj.getScaledWidth === 'function' ? obj.getScaledWidth() : (obj.width || 100);
        const h = typeof obj.getScaledHeight === 'function' ? obj.getScaledHeight() : (obj.height || 100);
        const fill = obj.fill;
        const stroke = obj.stroke;
        const strokeWidth = obj.strokeWidth;
        const angle = obj.angle || 0;
        let next: any = null;
        if (d.type === 'rect') {
          next = new FabricRect({ left: cx, top: cy, width: w, height: h, originX: 'center', originY: 'center', fill, stroke, strokeWidth, angle } as any);
        } else if (d.type === 'circle') {
          const r = Math.min(w, h) / 2;
          next = new FabricCircle({ left: cx, top: cy, radius: r, originX: 'center', originY: 'center', fill, stroke, strokeWidth, angle } as any);
        } else if (d.type === 'ellipse') {
          next = new FabricEllipse({ left: cx, top: cy, rx: w / 2, ry: h / 2, originX: 'center', originY: 'center', fill, stroke, strokeWidth, angle } as any);
        } else if (d.type === 'polygon') {
          const points = [{ x: -w / 2, y: -h / 2 }, { x: w / 2, y: -h / 2 }, { x: w / 2, y: h / 2 }, { x: -w / 2, y: h / 2 }];
          next = new FabricPolygon(points as any, { left: cx, top: cy, originX: 'center', originY: 'center', fill, stroke, strokeWidth, angle } as any);
        }
        if (next) {
          canvas.remove(obj);
          hideLinearScaleControls(next);
          canvas.add(next);
          canvas.setActiveObject(next);
          next.setCoords();
          canvas.requestRenderAll();
          return;
        }
      }

      if (d.ai) {
        try {
          console.log("renderLayerOnFabricCanvas..",obj)
          obj.ai = d.ai;
          renderLayerOnFabricCanvas(canvas, obj, currrentLayerIndex);
        } catch { }
      } else if (obj.type === 'textbox' || obj.type === 'i-text') {
        if (typeof d.text !== 'undefined') obj.set({ text: d.text });
        if (typeof d.fontSize === 'number') obj.set({ fontSize: d.fontSize });
        if (typeof d.fontFamily !== 'undefined') obj.set({ fontFamily: d.fontFamily });

        if (d.effectType) {
          // Disable all effects first to ensure a clean state
          disableTextWarping(obj);
          disable3dEffect(obj);
          disableSplineWarp(obj);
          disableDotsEffect(obj);
          disableBezierWarp(obj);
          disableMultiColor(obj);
          disableCircularWarp(obj);
          disableLiquidWarp(obj);

          // Enable the new effect
          if (d.effectType === 'warp') {
            enableTextWarping(obj, d.gridSize, d.gridPoints || []);
          } if (d.effectType === 'extrude') {
            enable3dEffect(obj, d.effectOptions || {});
          } else if (d.effectType === 'spline') {
            enableSplineWarp(obj, d.splineGridSize || 3, d.srcPts || [], d.dstPts || []);
          } else if (d.effectType === 'dots') {
            enableDotsEffect(obj);
          } else if (d.effectType === 'bezier') {
            const bezierControls = d.bezierControls && Object.keys(d.bezierControls).length > 0 ? d.bezierControls : undefined;
            enableBezierWarp(obj, bezierControls, d.baselineOffset || 0);
          } else if (d.effectType === 'multicolor') {
            enableMultiColor(obj);
          } else if (d.effectType === 'circular') {
            enableCircularWarp(obj, {
              radius: d.circularRadius,
              rotations: d.circularRotations,
              rotationOffset: d.circularAngle
            });
          } else if (d.effectType === 'liquidwave') {
            enableLiquidWarp(obj, {
              radius: d.liquidRadius,
              flowStrength: d.liquidStrength,
              noiseStrength: d.liquidAngle
            });
          }
        }

      } else if (obj.type === 'image' && typeof d.url === 'string' && d.url) {
        try {
          const img = await FabricImage.fromURL(d.url, { crossOrigin: 'anonymous' } as any);
          const iw = img.width || 1;
          const ih = img.height || 1;
          const sX = obj.getScaledWidth ? obj.getScaledWidth() / iw : 1;
          const sY = obj.getScaledHeight ? obj.getScaledHeight() / ih : 1;
          img.set({ id: obj.id, left: obj.left, top: obj.top, originX: obj.originX, originY: obj.originY, scaleX: sX, scaleY: sY } as any);
          canvas.remove(obj);
          canvas.add(img);
          canvas.setActiveObject(img);
        } catch { }
      }
      obj.setCoords();
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:updateActiveObject', updateActiveHandler as EventListener);

    const deleteActiveHandler = (e: Event) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj = canvas.getActiveObject();
      if (!obj) return;
      canvas.remove(obj);
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:deleteActiveObject', deleteActiveHandler as EventListener);

    const deselectHandler = (e: Event) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      canvas.discardActiveObject();
      canvas.requestRenderAll();
    };
    window.addEventListener('giftmkr:deselect', deselectHandler as EventListener);

    const alignHandler = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      const op = d.op as string;
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj: any = canvas.getActiveObject();
      if (!obj) return;
      const w = typeof obj.getScaledWidth === 'function' ? obj.getScaledWidth() : (obj.width || 0);
      const h = typeof obj.getScaledHeight === 'function' ? obj.getScaledHeight() : (obj.height || 0);
      const cw = canvas.getWidth();
      const ch = canvas.getHeight();
      if (op === 'left') obj.set({ left: w / 2 });
      else if (op === 'center') obj.set({ left: cw / 2 });
      else if (op === 'right') obj.set({ left: cw - w / 2 });
      else if (op === 'top') obj.set({ top: h / 2 });
      else if (op === 'middle') obj.set({ top: ch / 2 });
      else if (op === 'bottom') obj.set({ top: ch - h / 2 });
      obj.setCoords();
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:align', alignHandler as EventListener);

    const lockHandler = (e: Event) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj: any = canvas.getActiveObject();
      if (!obj) return;
      obj.set({
        lockMovementX: true,
        lockMovementY: true,
        lockScalingX: true,
        lockScalingY: true,
        lockRotation: true,
        hasControls: false
      });
      obj.setCoords();
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:lockLayer', lockHandler as EventListener);

    const unlockHandler = (e: Event) => {
      const canvas = fabricCanvasRef.current;
      if (!canvas) return;
      const obj: any = canvas.getActiveObject();
      if (!obj) return;
      obj.set({
        lockMovementX: false,
        lockMovementY: false,
        lockScalingX: false,
        lockScalingY: false,
        lockRotation: false,
        hasControls: true
      });
      obj.setCoords();
      canvas.requestRenderAll();
      pushHistory();
    };
    window.addEventListener('giftmkr:unlockLayer', unlockHandler as EventListener);
    const selectIndexHandler = (e: Event) => {
      console.log("selectIndexHandler", e)
      const d = (e as CustomEvent).detail || {};
      const idx = Number(d.index);
      if (!Number.isInteger(idx)) return;
      const target = canvas.getObjects()[idx];
      if (!target) return;
      canvas.setActiveObject(target as any);
      canvas.requestRenderAll();
      const detail = typeof (target as any).toObject === 'function' ? (target as any).toObject() : target;
      if ((target as any).data) (detail as any).data = (target as any).data;
      const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail });
      window.dispatchEvent(ev);
    };
    window.addEventListener('giftmkr:selectLayerIndex', selectIndexHandler as EventListener);
    canvas.on('selection:created', (e: any) => {
      const obj: any = canvas.getActiveObject();
      console.log("created", e)
      if (!obj) return;
      if (onLayerClick) {
        onLayerClick(obj.id, e.e);
      }
      hideLinearScaleControls(obj);
      const detail = typeof obj.toObject === 'function' ? obj.toObject() : obj;
      if ((obj as any).data) (detail as any).data = (obj as any).data;
      if (obj.ai) {
        detail.ai = obj.ai;
      }
      const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail });
      window.dispatchEvent(ev);
    });
    canvas.on('selection:updated', (e: any) => {
      console.log("updated", e)
      const obj: any = canvas.getActiveObject();
      if (!obj) return;
      if (onLayerClick) {
        onLayerClick(obj.id, e.e);
      }
      hideLinearScaleControls(obj);
      const detail = typeof obj.toObject === 'function' ? obj.toObject() : obj;
      if ((obj as any).data) (detail as any).data = (obj as any).data;
      if (obj.ai) {
        detail.ai = obj.ai;
      }
      const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail });
      window.dispatchEvent(ev);
    });
    canvas.on('selection:cleared', (e: any) => {
      console.log("canvas cleared", e)
      // if (onCanvasClick) {
      //     // @ts-ignore
      //     onCanvasClick(e.e);
      // }
      // const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail: null });
      // window.dispatchEvent(ev);
    });
    canvas.on('object:rotating', (e: any) => {
      const obj: any = e?.target || canvas.getActiveObject();
      if (!obj) return;
      const detail = typeof obj.toObject === 'function' ? obj.toObject() : obj;
      if ((obj as any).data) (detail as any).data = (obj as any).data;
      if (obj.ai) {
        detail.ai = obj.ai;
      }
      const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail });
      window.dispatchEvent(ev);
    });
    canvas.on('object:modified', (e: any) => {
      console.log("object modified", e)
      const obj: any = e?.target || canvas.getActiveObject();
      if (!obj) return;
      const detail = typeof obj.toObject === 'function' ? obj.toObject() : obj;
      if ((obj as any).data) (detail as any).data = (obj as any).data;
      if (obj.ai) {
        detail.ai = obj.ai;
      }
      const ev = new CustomEvent('giftmkr:activeObjectChanged', { detail });
      window.dispatchEvent(ev);
      pushHistory();
    });
    return () => {
      try { canvas.dispose(); } catch { }
      fabricCanvasRef.current = null;
      window.removeEventListener('giftmkr:addShape', handler as EventListener);
      window.removeEventListener('giftmkr:addText', textHandler as EventListener);
      window.removeEventListener('giftmkr:addImage', imageHandler as EventListener);
      window.removeEventListener('giftmkr:setBackgroundTexture', bgTextureHandler as EventListener);
      window.removeEventListener('giftmkr:setBackgroundImage', bgImageHandler as EventListener);
      window.removeEventListener('giftmkr:setBackgroundColor', bgColorHandler as EventListener);
      window.removeEventListener('giftmkr:align', alignHandler as EventListener);
      window.removeEventListener('giftmkr:lockLayer', lockHandler as EventListener);
      window.removeEventListener('giftmkr:unlockLayer', unlockHandler as EventListener);
      window.removeEventListener('giftmkr:selectLayerIndex', selectIndexHandler as EventListener);
      window.removeEventListener('giftmkr:updateActiveObject', updateActiveHandler as EventListener);
      window.removeEventListener('giftmkr:deleteActiveObject', deleteActiveHandler as EventListener);
      window.removeEventListener('giftmkr:deselect', deselectHandler as EventListener);
      window.removeEventListener('giftmkr:undo', undoHandler as EventListener);
      window.removeEventListener('giftmkr:redo', redoHandler as EventListener);
      window.removeEventListener('giftmkr:bringToFront', bringToFrontHandler as EventListener);
      window.removeEventListener('giftmkr:sendToBack', sendToBackHandler as EventListener);
    };
  }, [canvasSize.width, canvasSize.height]);

  return (
    <div className="flex-1 flex items-center justify-center p-4 relative">
      <div
        className="relative bg-white shadow-lg border border-gray-300 overflow-hidden select-none"
        style={{
          width: `${canvasSize.width}px`,
          height: `${canvasSize.height}px`,
          backgroundColor: selectedColorObj?.hex || '#ffffff',
          maxWidth: '100%',
          maxHeight: '100%'
        }}
      >
        <canvas ref={layersCanvasRef} style={{ width: '100%', height: '100%', display: 'block' }} />
      </div>
    </div>
  );
};

export default Canvas;
function transformCanvasObjectsForSnapshot(canvas: any) {
  return canvas.getObjects().map((o: any) => {
    const j = typeof o.toObject === 'function' ? o.toObject() : { ...o };
    if (j.fill && j.fill.source) {
      j.fill.source = '';
      j.fill.src = (o.data && o.data.fillSrc) ? o.data.fillSrc : j.fill.src;
    }
    j.id = o.id;
    const effectInfo = getEffectInfo(o);

    const lockMovementX = !!o.lockMovementX;
    const lockMovementY = !!o.lockMovementY;
    const lockScalingX = !!o.lockScalingX;
    const lockScalingY = !!o.lockScalingY;
    const lockRotation = !!o.lockRotation;
    const hasControls = o.hasControls === false ? false : true;
    const locked = (hasControls === false) || lockRotation || lockMovementX || lockMovementY || lockScalingX || lockScalingY;
    return { ...j, ...effectInfo, hasControls, locked };
  });
}
